import { TextField, Typography, Box, Button, FormControlLabel, Checkbox } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import integrationsService from 'services/api/integrations.service';
import SglnField from 'components/integrations/SglnField';
import { integrationErrorMessage } from './integration-user-messages';

export interface IEpcisIdentityValidation {
    glnValid?: boolean;
    glnCheckDigitOk?: boolean;
    glnMessage?: string;
    sglnValid?: boolean;
    sglnMessage?: string;
    sglnPreviewUrn?: string;
    configuredGln?: string;
    configuredSgln?: string;
    configuredCompanyPrefix?: string;
}

export interface IEpcisDataHealth {
    pharmacyGlnWithoutSgln?: number;
    supplierGlnWithoutSgln?: number;
    pharmaciesMissingPartyAddress?: number;
}

const readOnlyInputProps = { readOnly: true as const };

const EpcisCompanySettingsPanel: FC = () => {
    const [gln, setGln] = useState('');
    const [companyPrefix, setCompanyPrefix] = useState('');
    const [sgln, setSgln] = useState('');
    const [validation, setValidation] = useState<IEpcisIdentityValidation | null>(null);
    const [dataHealth, setDataHealth] = useState<IEpcisDataHealth | null>(null);
    const [loading, setLoading] = useState(false);
    const [strictSemantic, setStrictSemantic] = useState(false);
    const [shipStrictMode, setShipStrictMode] = useState(false);
    const [healthcareStructuralFallback, setHealthcareStructuralFallback] = useState(true);
    const [savingStrict, setSavingStrict] = useState(false);
    const [savingShipStrict, setSavingShipStrict] = useState(false);
    const [savingFallback, setSavingFallback] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [vRes, hRes, settingsRes]: any[] = await Promise.all([
                integrationsService.validateEpcisIdentity(),
                integrationsService.getEpcisDataHealth(),
                integrationsService.getPlatformSettings(),
            ]);
            const v = vRes.data.validation as IEpcisIdentityValidation;
            setValidation(v);
            setGln(v.configuredGln || '');
            setSgln(v.configuredSgln || '');
            setCompanyPrefix(v.configuredCompanyPrefix || '');
            setDataHealth(hRes.data.dataHealth || null);
            const settings: { key?: string; value?: string }[] = settingsRes.data?.platformSettings || [];
            const strict = settings.find((s) => s.key === 'epcis.validation.strict_semantic');
            setStrictSemantic(strict?.value === 'true' || strict?.value === '1');
            const shipStrict = settings.find((s) => s.key === 'epcis.ship.strictMode');
            setShipStrictMode(shipStrict?.value === 'true' || shipStrict?.value === '1');
            const fallback = settings.find((s) => s.key === 'epcis.validation.healthcare_structural_fallback');
            setHealthcareStructuralFallback(
                fallback?.value === undefined || fallback?.value === 'true' || fallback?.value === '1'
            );
        } catch (err) {
            toast.error(integrationErrorMessage(err, 'Could not load EPCIS company identity.'), { autoClose: 5000 });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const refreshValidation = async () => {
        try {
            const r: any = await integrationsService.validateEpcisIdentity();
            setValidation(r.data.validation);
            toast.success('EPCIS identity refreshed', { autoClose: 3000 });
        } catch (err) {
            toast.error(integrationErrorMessage(err, 'Validation failed.'), { autoClose: 5000 });
        }
    };

    const saveStrictSemantic = async (enabled: boolean) => {
        setSavingStrict(true);
        try {
            await integrationsService.putPlatformSettings([
                { key: 'epcis.validation.strict_semantic', value: enabled ? 'true' : 'false' },
            ]);
            setStrictSemantic(enabled);
            toast.success(enabled ? 'Strict inbound validation enabled' : 'Lenient inbound validation enabled');
        } catch (err) {
            toast.error(integrationErrorMessage(err, 'Could not update validation profile.'));
        } finally {
            setSavingStrict(false);
        }
    };

    const saveShipStrictMode = async (enabled: boolean) => {
        setSavingShipStrict(true);
        try {
            await integrationsService.putPlatformSettings([
                { key: 'epcis.ship.strictMode', value: enabled ? 'true' : 'false' },
            ]);
            setShipStrictMode(enabled);
            toast.success(enabled ? 'Outbound ship strict mode enabled' : 'Outbound ship strict mode disabled');
        } catch (err) {
            toast.error(integrationErrorMessage(err, 'Could not update ship strict mode.'));
        } finally {
            setSavingShipStrict(false);
        }
    };

    const saveHealthcareFallback = async (enabled: boolean) => {
        setSavingFallback(true);
        try {
            await integrationsService.putPlatformSettings([
                {
                    key: 'epcis.validation.healthcare_structural_fallback',
                    value: enabled ? 'true' : 'false',
                },
            ]);
            setHealthcareStructuralFallback(enabled);
            toast.success(
                enabled
                    ? 'Healthcare XSD structural fallback enabled'
                    : 'Strict healthcare XSD validation enabled'
            );
        } catch (err) {
            toast.error(integrationErrorMessage(err, 'Could not update L2 validation profile.'));
        } finally {
            setSavingFallback(false);
        }
    };

    return (
        <div className="admin-motion-card rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <Typography variant="h6" className="mb-2 font-semibold text-slate-800">
                EPCIS company identity
            </Typography>
            <Typography variant="body2" color="text.secondary" className="mb-4">
                GLN and SGLN are configured for deployment (platform settings / environment). They are shown here
                for verification only — use inbound validation toggles below to adjust receive behavior.
            </Typography>
            {dataHealth ? (
                <Typography variant="body2" className="mb-3 text-amber-800">
                    Data health: {dataHealth.pharmacyGlnWithoutSgln ?? 0} pharmacy account(s) missing SGLN;{' '}
                    {dataHealth.supplierGlnWithoutSgln ?? 0} supplier(s) missing SGLN;{' '}
                    {dataHealth.pharmaciesMissingPartyAddress ?? 0} pharmacy account(s) missing ship-to address.
                </Typography>
            ) : null}
            <Box className="grid gap-3 md:grid-cols-2">
                <TextField
                    size="small"
                    fullWidth
                    label="Company GLN (13 digits)"
                    value={gln}
                    InputProps={readOnlyInputProps}
                />
                <TextField
                    size="small"
                    fullWidth
                    label="Company prefix (metadata)"
                    value={companyPrefix}
                    InputProps={readOnlyInputProps}
                />
                <div className="md:col-span-2">
                    <SglnField
                        id="companySgln"
                        label="Company SGLN"
                        value={sgln}
                        onChange={() => {}}
                        readOnly
                        helperText="Used in outbound EPCIS and inbound receiver matching"
                    />
                </div>
            </Box>
            {validation ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    GLN: {validation.glnValid ? 'valid' : validation.glnMessage || 'invalid'}
                    {validation.glnCheckDigitOk === false ? ' (check digit)' : ''} · SGLN:{' '}
                    {validation.sglnValid ? validation.sglnPreviewUrn || 'valid' : validation.sglnMessage}
                </Typography>
            ) : null}
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button variant="outlined" size="small" onClick={refreshValidation} disabled={loading}>
                    Refresh
                </Button>
            </Box>
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" className="mb-1 font-semibold text-slate-800">
                    Inbound validation (production)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Enable strict semantic validation in production so L3 warnings block SFTP/AS2 approve. Healthcare
                    Healthcare EPCIS 1.2 XML is fully validated at L1–L4 before approve.
                </Typography>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={strictSemantic}
                            disabled={savingStrict || loading}
                            onChange={(e) => saveStrictSemantic(e.target.checked)}
                        />
                    }
                    label="Strict inbound validation (semantic warnings block approve)"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mb: 2 }}>
                    When off (default on existing environments), XSD-valid files with semantic warnings can still be
                    approved. New environments default to strict via migration +26.
                </Typography>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={healthcareStructuralFallback}
                            disabled={savingFallback || loading}
                            onChange={(e) => saveHealthcareFallback(e.target.checked)}
                        />
                    }
                    label="Healthcare 1.2 XSD structural fallback (L2)"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                    When on, partner healthcare XML that fails strict XSD may pass L2 with a warning. Turn off for
                    stricter schema enforcement.
                </Typography>
                <Typography variant="subtitle2" className="mb-1 mt-3 font-semibold text-slate-800">
                    Outbound shipping (production)
                </Typography>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={shipStrictMode}
                            disabled={savingShipStrict || loading}
                            onChange={(e) => saveShipStrictMode(e.target.checked)}
                        />
                    }
                    label="Strict outbound ship mode (fail ship if EPCIS generation or L4 validation fails)"
                />
            </Box>
        </div>
    );
};

export default EpcisCompanySettingsPanel;
